// import dairyRewardImage from "@/assets/images/daily-reward/dairy-reward.jpg";
import dailyRewardBg from "@/assets/images/daily-reward/daily-reward-bg.jpg";
import dailyRewardButton from "@/assets/images/daily-reward/daily-reward-button.png";
import dailyRewardChecked from "@/assets/images/daily-reward/daily-reward-checked.png";
import AuthContext from "@/context/auth";
import useAPI from "@/libs/hooks/api";
import useVisible from "@/libs/hooks/visible";
import {
  Box,
  Center,
  Circle,
  Grid,
  GridItem,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Square,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

const DAILY_REWARD_Z_INDEX = 1810;

const Stamp = (props) => (
  <Square position="relative" {...props}>
    <Text transform="rotate(20.12deg)" color="white" fontSize="12">
      <Trans i18nKey="daily_rewards.claimed" />
    </Text>
    <Circle position="absolute" size="58px" border="1px #D9D9D9 solid" />
    <Circle position="absolute" size="64px" border="1px #D9D9D9 solid" />
  </Square>
);

const DailyRewardTable = ({ rewards, currentDay, totalDays }) => {
  const getDayBackground = (day) => {
    if (day < currentDay) {
      return "rgba(246, 247, 248, 1)"; // checked days
    }
    if (day === currentDay) {
      return "rgba(148, 219, 16, 1)"; // today
    }
    if (day % 7 === 0) {
      return "rgba(253, 103, 183, 1)"; // 7th day
    }
    if (day === totalDays) {
      return "rgba(205, 103, 253, 1)"; // final day
    }
    return "rgba(243, 244, 246, 1)"; // normal day
  };

  const getDayContent = (day) => {
    if (day < currentDay || day === currentDay) {
      return (
        <Image
          src={dailyRewardChecked}
          alt="checked"
          width="15px"
          height="11px"
        />
      );
    }
    return (
      <>
        <Text fontSize="6.5px" color={day % 7 === 0 || day % totalDays === 0 ? "white" : "rgba(156, 158, 161, 1)"}>
          第{day}天
        </Text>
        <Text
          fontSize={day % 7 === 0 ? "14px" : "12px"}
          color={day % 7 === 0 || day % totalDays === 0 ? "white" : "rgba(253, 103, 183, 1)"}
          fontWeight="bold"
        >
          +{rewards[day - 1] || 0}
        </Text>
      </>
    );
  };

  const getDayColor = (day) => {
    if (day < currentDay || day === currentDay) {
      return "rgba(156, 158, 161, 1)";
    }
    return "rgba(156, 158, 161, 1)";
  };

  const getDaySize = (day) => {
    if (day % 7 === 0) {
      return "41px"; // 7th day
    }
    if (day === totalDays) {
      return "41px"; // final day
    }
    return "33px"; // normal day
  };

  const getDayShape = (day) => {
    if (day % 7 === 0 || day === totalDays) {
      return "circle"; // 7th day and final day are circles
    }
    return "rectangle"; // normal days are rectangles
  };

  return (
    <Grid width="302px" templateColumns="repeat(7, 1fr)" p={2}>
      {Array.from({ length: 35 }, (_, index) => {
        const day = index + 1;
        const isVisible = day <= totalDays;
        
        if (!isVisible) return <GridItem key={day} />;
        
        const size = getDaySize(day);
        const shape = getDayShape(day);
        
        return (
          <GridItem key={day} py={2}>
            {shape === "circle" ? (
              <Circle
                size={size}
                bg={getDayBackground(day)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                flexDirection="column"
              >
                {getDayContent(day)}
              </Circle>
            ) : (
              <Box
                width={size}
                height="41px"
                bg={getDayBackground(day)}
                borderRadius="70px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                flexDirection="column"
              >
                {getDayContent(day)}
              </Box>
            )}
          </GridItem>
        );
      })}
    </Grid>
  );
};

const DailyReward = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const api = useAPI();
  const visible = useVisible();
  const [accumulation, setAccumulation] = useState(0);
  const [currentDay, setCurrentDay] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [rule, setRule] = useState({ rewards: [] });
  const [reward, setReward] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const vip = !!user?.isVip;

  const total = useMemo(
    () => rule.rewards?.reduce((acc, cur) => acc + cur + (vip ? 500 : 0), 0),
    [rule, vip],
  );

  const onUserBackToApp = useCallback(async () => {
    setIsLoading(true);
    try {
      //TODO disable daily reward for now
      return onClose();
      const result = await api.checkIn();
      if (result && Object.keys(result).length > 0) {
        // Check if we got a successful check-in response
        setAccumulation(result.accumulation || 0);
        setCurrentDay(result.currentDay || 0);
        setTotalDays(result.totalDays || 0);
        setRule(result.rule || { rewards: [] });
        setReward(result.reward || 0);
        onOpen();
      }
    } catch (e) {
      console.error("Check-in failed:", e);
    } finally {
      setIsLoading(false);
    }
  }, [api, onOpen]);

  useEffect(() => {
    if (visible) onUserBackToApp();
  }, [onUserBackToApp, visible]);

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay
        bg="rgba(0,0,0,.92)"
        zIndex={DAILY_REWARD_Z_INDEX}
        onClick={onClose}
      />
      <ModalBody
        zIndex={DAILY_REWARD_Z_INDEX + 1}
        position="relative"
        onClick={onClose}
      >
        <ModalContent bg="transparent" onClick={onClose}>
          <Center>
            <Box position="relative" onClick={onClose}>
              <Image
                width="326px"
                pt={vip ? "20px" : 0}
                src={dailyRewardBg}
                onClick={onClose}
              />
              <Text
                position="absolute"
                top="40px"
                left="20px"
                fontSize="2xl"
                fontWeight="bold"
                color="rgba(51, 41, 44, 1)" 
                zIndex={1}
              >
                每日簽到領鑽石
              </Text>
              {/* Daily Reward Table */}
              <Box position="absolute"
                top="50%"
                left="50%"
                transform="translate(calc(-50%), calc(-50% + 5px))"
                >
                <DailyRewardTable 
                  rewards={rule.rewards || []} 
                  currentDay={currentDay} 
                  totalDays={totalDays} 
                />
              </Box>
              <Image src={dailyRewardButton}
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(calc(-50%), calc(-50% + 180px))"
                width="282px"
                height="52px"
               />

            </Box>
          </Center>
          
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

DailyReward.Z_INDEX = DAILY_REWARD_Z_INDEX;

export default DailyReward;